import * as z from 'zod';

// Base schema with common fields for all pension plan types
const basePensionPlanSchema = z.object({
  fundName: z
    .string()
    .min(1, 'Fund name is required')
    .max(100, 'Fund name must be 100 characters or less'),
  accountNumber: z
    .string()
    .min(1, 'Account number is required')
    .max(50, 'Account number must be 50 characters or less'),
  currency: z.string().min(1, 'Currency is required'),
  interestRate: z
    .number()
    .min(0, 'Interest rate must be 0 or greater')
    .max(100, 'Interest rate must be 100 or less'),
  startingAmount: z.number().positive('Starting amount must be greater than 0'),
  notes: z.string().max(500, 'Notes must be 500 characters or less').optional(),
  description: z.string().max(500, 'Description must be 500 characters or less').optional(),
  pensionType: z.enum(['government', 'occupational', 'individual']),
});

// Government pension schema with country and fund manager validation
const governmentPensionSchema = basePensionPlanSchema.extend({
  pensionType: z.literal('government'),
  country: z.string().min(1, 'Country is required for government pension plans'),
  fundManagerId: z.string().min(1, 'Please select a fund manager or add a new one').optional().or(z.literal('')),
  newFundManagerName: z.string().max(100, 'Fund manager name must be 100 characters or less').optional(),
  fundManager: z.string().optional(),
  employerName: z.string().optional(),
  employerContributionPercentage: z.number().optional(),
  employeeContributionPercentage: z.number().optional(),
});

// Occupational pension schema with employer fields and contribution validation
const occupationalPensionSchema = basePensionPlanSchema.extend({
  pensionType: z.literal('occupational'),
  fundManager: z.string().min(1, 'Fund manager is required').max(100, 'Fund manager must be 100 characters or less'),
  employerName: z.string().min(1, 'Employer name is required').max(100, 'Employer name must be 100 characters or less'),
  employerContributionPercentage: z
    .number()
    .min(0, 'Employer contribution must be between 0 and 100')
    .max(100, 'Employer contribution must be between 0 and 100'),
  employeeContributionPercentage: z
    .number()
    .min(0, 'Employee contribution must be between 0 and 100')
    .max(100, 'Employee contribution must be between 0 and 100'),
  country: z.string().optional(),
  fundManagerId: z.string().optional(),
  newFundManagerName: z.string().optional(),
});

// Individual pension schema with standard fields
const individualPensionSchema = basePensionPlanSchema.extend({
  pensionType: z.literal('individual'),
  fundManager: z.string().min(1, 'Fund manager is required').max(100, 'Fund manager must be 100 characters or less'),
  country: z.string().optional(),
  fundManagerId: z.string().optional(),
  newFundManager_name: z.string().optional(),
  employerName: z.string().optional(),
  employerContributionPercentage: z.number().optional(),
  employeeContributionPercentage: z.number().optional(),
});

// Union schema that validates based on pension_type
export const pensionPlanFormSchema = z
  .discriminatedUnion('pensionType', [
    occupationalPensionSchema,
    governmentPensionSchema,
    individualPensionSchema,
  ])
  .refine(
    (data) => {
      // For government pensions, either fund_manager_id or new_fund_manager_name must be provided
      if (data.pensionType === 'government') {
        return !!data.fundManagerId || !!data.newFundManagerName;
      }
      return true;
    },
    {
      message: 'Please select a fund manager or add a new one',
      path: ['fundManagerId'],
    }
  );

export type PensionPlanFormSchema = z.infer<typeof pensionPlanFormSchema>;
